import type { IconName } from '@w7/ui'

/**
 * Lab Bookmaker-UI (production) sidebar — order and labels from header.html.
 * Paths match Lab ui-router URLs so deep links stay familiar.
 */

export interface NavItem {
  id: string
  label: string
  to: string
  icon: IconName
  match?: string
  description: string
  /** Lab `ng-if="level=='Admin'"` */
  adminOnly?: boolean
}

export interface NavGroup {
  id: string
  /** Empty label = flat top-level Lab menu-heads (no accordion title). */
  label: string
  items: NavItem[]
  /** Lab Create accordion is Admin-only. */
  adminOnly?: boolean
  /** Lab Create / List: collapsible accordion (not always-open section). */
  accordion?: boolean
}

export const NAV: NavGroup[] = [
  {
    id: 'top',
    label: '',
    items: [
      {
        id: 'in-play',
        label: 'In Play',
        to: '/InPlay',
        icon: 'exposure',
        match: '/InPlay',
        description: 'In-play markets',
      },
      {
        id: 'users',
        label: 'User',
        to: '/user',
        icon: 'users',
        match: '/user',
        description: 'Admin users',
        adminOnly: true,
      },
      {
        id: 'rates',
        label: 'Rates',
        to: '/rates',
        icon: 'commission',
        description: 'Rate presets',
        adminOnly: true,
      },
      {
        id: 'ratesdiff',
        label: 'RatesDiff',
        to: '/ratesdiff',
        icon: 'commission',
        description: 'Rate diff presets',
        adminOnly: true,
      },
      {
        id: 'both-rates',
        label: 'Both Rates',
        to: '/bothRates',
        icon: 'commission',
        description: 'Both rates CRUD',
        adminOnly: true,
      },
    ],
  },
  {
    id: 'create',
    label: 'Create',
    adminOnly: true,
    accordion: true,
    items: [
      {
        id: 'create-game',
        label: 'Game',
        to: '/creategame',
        icon: 'plus',
        description: 'Create game',
        adminOnly: true,
      },
      {
        id: 'create-comp',
        label: 'Competition',
        to: '/createcomp',
        icon: 'plus',
        description: 'Create competition',
      },
      {
        id: 'create-event',
        label: 'Event',
        to: '/createevent',
        icon: 'plus',
        description: 'Create event',
      },
      {
        id: 'create-market',
        label: 'Market',
        to: '/createmarket',
        icon: 'plus',
        description: 'Create market',
      },
      {
        id: 'create-jodi',
        label: 'Jodi Market',
        to: '/createjodimarket',
        icon: 'plus',
        description: 'Create jodi market',
      },
    ],
  },
  {
    id: 'list',
    label: 'List',
    accordion: true,
    items: [
      {
        id: 'games',
        label: 'Game',
        to: '/game',
        icon: 'overview',
        description: 'List games',
        adminOnly: true,
      },
      {
        id: 'comps',
        label: 'Competition',
        to: '/complist',
        icon: 'users',
        description: 'List competitions',
        adminOnly: true,
      },
      {
        id: 'events',
        label: 'Event',
        to: '/eventlist',
        icon: 'activity',
        description: 'List events',
        adminOnly: true,
      },
      {
        id: 'market-list',
        label: 'Market',
        to: '/marketlist',
        icon: 'net-exposure',
        match: '/marketlist',
        description: 'List markets',
      },
    ],
  },
  {
    id: 'more',
    label: '',
    items: [
      // Temporarily disabled — home is In Play
      // {
      //   id: 'market-select',
      //   label: 'Market Selection',
      //   to: '/marketSelect',
      //   icon: 'overview',
      //   match: '/marketSelect',
      //   description: 'Search games and open markets',
      // },
      {
        id: 'google-auth',
        label: 'Google Authentication',
        to: '/googleAuth',
        icon: 'shield',
        description: '2FA setup',
      },
      {
        id: 'turnover',
        label: 'Turnover',
        to: '/turnover',
        icon: 'turnover',
        description: 'Market turnover',
      },
    ],
  },
]

export const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((group) => group.items)

/** Visible nav for the current Lab admin level (`Admin` / other). */
export function navForLevel(level: string): NavGroup[] {
  const isAdmin = level.trim() === 'Admin' || level.trim() === ''
  return NAV.map((group) => {
    if (group.adminOnly && !isAdmin) return null
    const items = group.items.filter((item) => !item.adminOnly || isAdmin)
    if (!items.length) return null
    return { ...group, items }
  }).filter((g): g is NavGroup => g != null)
}
